/**
 * MifeLife v2 — Mentor auth pages.
 *  - V2MentorLoginPage  (/mentor/giris): email/password + Google, routes to /mentor/panel
 *  - V2MentorClaimPage  (/mentor/davet): set a password from an approval invite token
 */
import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Sparkles, Loader2, GraduationCap } from 'lucide-react';
import V2Nav from '../../components/v2/V2Nav';
import V2Footer from '../../components/v2/V2Footer';
import { apiRequest, setToken, setUser } from '../../lib/utils';
import { useNoIndex } from '../../hooks/useNoIndex';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleAsMentor = () => {
  // Tell the OAuth callback to route to the mentor panel after login.
  sessionStorage.setItem('mife_after_login', '/mentor/panel');
  const redirectUrl = window.location.origin + '/auth/callback';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const Shell = ({ title, sub, children }) => (
  <div className="min-h-screen bg-gradient-to-b from-[#0F0F1A] via-[#11111E] to-[#1A1A2E] text-white antialiased" data-testid="v2-mentor-auth-shell">
    <V2Nav lang="tr" />
    <section className="pt-32 pb-20">
      <div className="max-w-md mx-auto px-5 sm:px-8">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-400/30 text-[11px] uppercase tracking-[0.18em] text-violet-200 mb-5">
          <GraduationCap className="w-3.5 h-3.5" /> Mentör
        </div>
        <h1 className="text-3xl font-semibold tracking-tight">{title}</h1>
        <p className="mt-2 text-sm text-slate-400">{sub}</p>
        <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] p-6">{children}</div>
      </div>
    </section>
    <V2Footer lang="tr" />
  </div>
);

const Field = ({ label, type, value, onChange, testid }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-1.5">{label}</span>
    <input
      type={type} value={value} onChange={(e) => onChange(e.target.value)} required
      className="w-full h-11 px-3 rounded-lg bg-[#0a0a14] border border-white/10 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-violet-400/50 transition"
      data-testid={testid}
    />
  </label>
);

export function V2MentorLoginPage() {
  useNoIndex();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const res = await apiRequest('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });
      const tok = res?.access_token || res?.token;
      if (tok) setToken(tok);
      if (res?.user) setUser(res.user);
      nav('/mentor/panel');
    } catch (e2) {
      setErr(e2?.message || 'Giriş başarısız.');
    } finally { setBusy(false); }
  };

  return (
    <Shell title="Mentör girişi" sub="Profilini, fiyatlarını ve müsaitliğini yönet.">
      <form onSubmit={submit} className="space-y-3" data-testid="v2-mentor-login-form">
        <Field label="E-posta" type="email" value={email} onChange={setEmail} testid="v2-mentor-login-email" />
        <Field label="Şifre" type="password" value={password} onChange={setPassword} testid="v2-mentor-login-password" />
        {err && <div className="text-sm text-rose-300" data-testid="v2-mentor-login-error">{err}</div>}
        <button type="submit" disabled={busy} className="w-full h-11 rounded-lg bg-gradient-to-r from-violet-500 to-violet-600 text-white text-sm font-semibold hover:from-violet-400 hover:to-violet-500 disabled:opacity-50 transition flex items-center justify-center gap-2" data-testid="v2-mentor-login-submit">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Panele gir
        </button>
        <button type="button" onClick={startGoogleAsMentor} className="w-full h-11 rounded-lg border border-white/15 bg-white text-slate-900 text-sm font-medium hover:bg-slate-100 transition flex items-center justify-center gap-2" data-testid="v2-mentor-login-google">
          Google ile devam et
        </button>
      </form>
      <div className="mt-6 text-center text-sm text-slate-400">
        Henüz mentör değil misin? <Link to="/mentor-basvuru" className="text-violet-300 hover:text-white">Başvur</Link>
      </div>
    </Shell>
  );
}

export function V2MentorClaimPage() {
  useNoIndex();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { setErr('Şifre en az 6 karakter olmalı.'); return; }
    if (password !== confirm) { setErr('Şifreler eşleşmiyor.'); return; }
    setBusy(true); setErr(null);
    try {
      const res = await apiRequest('/v2/mentor/claim', { method: 'POST', body: JSON.stringify({ token, password }) });
      const tok = res?.access_token;
      if (tok) setToken(tok);
      if (res?.user) setUser(res.user);
      nav('/mentor/onboarding');
    } catch (e2) {
      setErr(e2?.message || 'Davet doğrulanamadı.');
    } finally { setBusy(false); }
  };

  if (!token) {
    return (
      <Shell title="Geçersiz davet" sub="Bu bağlantı eksik veya hatalı.">
        <p className="text-sm text-slate-400">Davet bağlantın çalışmıyor. Lütfen e-postandaki bağlantıyı tekrar kullan ya da <Link to="/mentor/giris" className="text-violet-300">giriş yap</Link>.</p>
      </Shell>
    );
  }

  return (
    <Shell title="Mentör hesabını aktive et" sub="Bir şifre belirle, panele gir.">
      <form onSubmit={submit} className="space-y-3" data-testid="v2-mentor-claim-form">
        <Field label="Yeni şifre" type="password" value={password} onChange={setPassword} testid="v2-mentor-claim-password" />
        <Field label="Şifre (tekrar)" type="password" value={confirm} onChange={setConfirm} testid="v2-mentor-claim-confirm" />
        {err && <div className="text-sm text-rose-300" data-testid="v2-mentor-claim-error">{err}</div>}
        <button type="submit" disabled={busy} className="w-full h-11 rounded-lg bg-gradient-to-r from-violet-500 to-violet-600 text-white text-sm font-semibold hover:from-violet-400 hover:to-violet-500 disabled:opacity-50 transition flex items-center justify-center gap-2" data-testid="v2-mentor-claim-submit">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Şifreyi belirle & devam et
        </button>
        <button type="button" onClick={startGoogleAsMentor} className="w-full h-11 rounded-lg border border-white/15 bg-white text-slate-900 text-sm font-medium hover:bg-slate-100 transition flex items-center justify-center gap-2" data-testid="v2-mentor-claim-google">
          Onun yerine Google ile gir
        </button>
      </form>
    </Shell>
  );
}
